/**
 * SB6-ANA-CACHE-01 — canonical-key full-res image cache (Cache Storage).
 *
 * A thin wrapper over the Cache Storage API (`caches.open('wp-images-v1')`)
 * that keys every entry by the **stable blob path** (see `canonicalKey.ts`)
 * rather than the signed URL, so cached bytes survive SAS rotation and browser
 * restarts (story §2.1-A/B).
 *
 * Design constraints from the story:
 * - **No service worker.** Used directly from the window context; the bucket
 *   name (`wp-images-v1`) is stable so a SW could adopt the same cache later
 *   (Q4) — nothing here assumes a window vs. worker context beyond `caches`.
 * - **Full resolution only.** thumb120/thumb900 stay on the browser HTTP cache.
 * - **Graceful degradation.** When `caches` is unavailable (insecure context,
 *   older browser, or a test environment without the API) every method falls
 *   through to a plain `fetch` and never throws for that reason.
 * - **Typed failures.** Non-OK fetches reject with `ImageFetchError` carrying
 *   the HTTP status; 403 (SAS expired) is the load-bearing case CACHE-02 keys
 *   off to re-mint the image list.
 */
import { canonicalKey } from "./canonicalKey.js";
import { recordCachedKey, touchKeyAccess } from "./manifestBridge.js";

/** Cache Storage bucket name. Stable so a future service worker can adopt it. */
export const IMAGE_CACHE_BUCKET = "wp-images-v1";

/**
 * Thrown by `fetchThrough` when the network fetch resolves with a non-OK
 * status. `status === 403` means an expired SAS URL — CACHE-02 catches this to
 * re-fetch the image list (which re-mints all SAS URLs) and retry.
 */
export class ImageFetchError extends Error {
  readonly status: number;
  readonly url: string;
  /** Convenience flag — a 403 from Azure means the SAS signature expired. */
  readonly isSasExpired: boolean;

  constructor(status: number, url: string, message?: string) {
    super(
      message ?? `Image fetch failed with status ${String(status)} for ${url}`,
    );
    this.name = "ImageFetchError";
    this.status = status;
    this.url = url;
    this.isSasExpired = status === 403;
    // Restore the prototype chain for `instanceof` under transpiled targets.
    Object.setPrototypeOf(this, ImageFetchError.prototype);
  }
}

/**
 * Is the Cache Storage API usable in this context?
 *
 * `caches` is only exposed in secure contexts (https / localhost). In an
 * insecure context, an old browser, or a bare test environment it's
 * `undefined` — callers then transparently fall through to plain `fetch`.
 */
function cachesAvailable(): boolean {
  return typeof caches !== "undefined";
}

/**
 * Open the image cache bucket, or `null` if Cache Storage is unavailable or
 * `open` itself throws/rejects (e.g. storage disabled). Never throws.
 */
async function openBucket(): Promise<Cache | null> {
  if (!cachesAvailable()) return null;
  try {
    return await caches.open(IMAGE_CACHE_BUCKET);
  } catch {
    return null;
  }
}

/**
 * The Request we store/look up under. Cache Storage matches on the request URL,
 * so we synthesise a same-origin URL from the canonical key. This makes the
 * stored entry independent of the (rotating) signed URL it was fetched from.
 *
 * The synthetic origin is irrelevant — we always look up via the same rule, so
 * the key just has to be internally consistent. We base it on the current
 * origin when one exists (window/worker) and fall back to a fixed scheme.
 */
function cacheRequestFor(key: string): Request {
  const base =
    typeof location !== "undefined" && location.origin
      ? location.origin
      : "https://wp-image-cache.invalid";
  // Encode the key into a single path segment so slashes in the blob path
  // don't have to resolve to a real URL hierarchy; the match is exact.
  const url = `${base}/__wp-image-cache__/${encodeURIComponent(key)}`;
  return new Request(url, { method: "GET" });
}

/**
 * Look up a cached blob by canonical key. Returns `null` on a miss, when Cache
 * Storage is unavailable, or on any internal error (never throws).
 */
export async function get(key: string): Promise<Blob | null> {
  const bucket = await openBucket();
  if (!bucket) return null;
  try {
    const hit = await bucket.match(cacheRequestFor(key));
    if (!hit) return null;
    // SB6-ANA-CACHE-03 — touch the dataset's recency on a cache READ (throttled
    // inside the manifest so image stepping doesn't thrash IndexedDB).
    touchKeyAccess(key);
    return await hit.blob();
  } catch {
    return null;
  }
}

/**
 * Store a fetched `Response` under the canonical key. The response is cloned
 * before reading so the caller can still consume its body. No-ops (without
 * throwing) when Cache Storage is unavailable or the response isn't cacheable.
 */
export async function put(key: string, response: Response): Promise<void> {
  const bucket = await openBucket();
  if (!bucket) return;
  // Only cache successful, non-opaque responses — an opaque (mode:"no-cors")
  // response has an unusable body, and a non-OK one is an error, not bytes.
  if (!response.ok || response.type === "opaque") return;
  try {
    await bucket.put(cacheRequestFor(key), response.clone());
  } catch {
    // QuotaExceededError or a disabled store — degrade silently; the bytes
    // are still returned to the caller, we just didn't persist them.
    return;
  }
  // SB6-ANA-CACHE-03 — bookkeeping: record the cached bytes into the per-
  // dataset manifest and evaluate the LRU budget. Best-effort + non-blocking;
  // the byte size comes from Content-Length when present (cheap, no body read),
  // else from the cloned blob's size (async). Never lets a bookkeeping failure
  // surface to the caller — the bytes are already cached.
  recordBytesForManifest(key, response);
}

/**
 * Resolve the byte size of a cached response and hand it to the manifest bridge.
 * Prefers the `Content-Length` header (no body read); falls back to reading the
 * blob size off a clone when the header is absent or unparseable.
 */
function recordBytesForManifest(key: string, response: Response): void {
  const header = response.headers.get("content-length");
  const fromHeader = header ? Number.parseInt(header, 10) : NaN;
  if (Number.isFinite(fromHeader) && fromHeader >= 0) {
    recordCachedKey(key, fromHeader);
    return;
  }
  // No usable Content-Length — measure the blob asynchronously. Cloning is safe
  // (we already consumed nothing of this `response` for the put above used its
  // own clone), and a failed read just records 0 bytes.
  void (async () => {
    try {
      const blob = await response.clone().blob();
      recordCachedKey(key, blob.size);
    } catch {
      recordCachedKey(key, 0);
    }
  })();
}

/**
 * Cache-through fetch.
 *
 * 1. Derive the canonical key from `signedUrl`.
 * 2. Cache hit → return the cached blob (zero network).
 * 3. Miss → `fetch(signedUrl, { mode: "cors" })`:
 *    - non-OK → reject with `ImageFetchError` (403 = SAS expired, distinguishable).
 *    - OK → `put` under the canonical key, return the blob.
 *
 * When Cache Storage is unavailable the hit step is skipped and the put is a
 * no-op — the function still performs the fetch and returns the blob.
 */
export async function fetchThrough(signedUrl: string): Promise<Blob> {
  const key = canonicalKey(signedUrl);

  const cached = await get(key);
  if (cached) return cached;

  const response = await fetch(signedUrl, { mode: "cors" });
  if (!response.ok) {
    throw new ImageFetchError(response.status, signedUrl);
  }

  // Persist for next time (best-effort), then hand the blob back. We clone for
  // the cache inside `put`, and read our own copy here.
  await put(key, response.clone());
  return await response.blob();
}

/**
 * Delete every entry in the image cache bucket. Used by CACHE-03's "clear all"
 * and by tests; safe no-op when Cache Storage is unavailable.
 */
export async function clearAll(): Promise<void> {
  if (!cachesAvailable()) return;
  try {
    await caches.delete(IMAGE_CACHE_BUCKET);
  } catch {
    /* nothing to clean up */
  }
}

/**
 * Delete a specific set of canonical keys from the image cache bucket. Used by
 * CACHE-03's LRU evictor and per-dataset "clear" to remove exactly one
 * dataset's entries (never a partial-by-image eviction — the caller passes the
 * dataset's WHOLE key set). Safe no-op when Cache Storage is unavailable;
 * returns the count of entries actually deleted.
 */
export async function deleteKeys(keys: readonly string[]): Promise<number> {
  const bucket = await openBucket();
  if (!bucket || keys.length === 0) return 0;
  let deleted = 0;
  for (const key of keys) {
    try {
      const ok = await bucket.delete(cacheRequestFor(key));
      if (ok) deleted += 1;
    } catch {
      /* skip this key, keep going */
    }
  }
  return deleted;
}

export const imageCache = {
  bucket: IMAGE_CACHE_BUCKET,
  get,
  put,
  fetchThrough,
  clearAll,
  deleteKeys,
};
